import { oc } from '@orpc/contract';
import { z } from 'zod';
import { chatFilter } from '../common/filters/chat';
import paginated from '../common/paginated.dto';
import reference from '../common/reference.dto';
import chat, {
  chatByAccessCode,
  chatDetails,
  chatTimelineEntry,
  getChatMessages,
  getChatMessagesByAccessCode,
  includedPerson,
  postChat,
  sendChat,
  updateChat,
} from './chat.dto';

export const chatContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/chat',
      summary: 'Get all chats',
    })
    .input(chatFilter)
    .output(paginated(chat)),

  byAccessCode: oc
    .route({
      method: 'GET',
      path: '/chat/link/{accessCode}',
      summary: 'Get chat by access code',
    })
    .input(z.object({ accessCode: z.string() }))
    .output(chatByAccessCode),

  getMessagesByAccessCode: oc
    .route({
      method: 'GET',
      path: '/chat/link/{accessCode}/message',
      summary: 'Get chat messages by access code',
    })
    .input(getChatMessagesByAccessCode)
    .output(paginated(chatTimelineEntry)),

  sendMessageByAccessCode: oc
    .route({
      method: 'POST',
      path: '/chat/link/{accessCode}/message',
      summary: 'Send message by access code',
    })
    .input(
      z.object({
        accessCode: z.string(),
        ...sendChat.shape,
      }),
    )
    .output(chatTimelineEntry),

  leaveByAccessCode: oc
    .route({
      method: 'POST',
      path: '/chat/link/{accessCode}/leave',
      summary: 'Leave chat by access code',
    })
    .input(z.object({ accessCode: z.string() }))
    .output(chatByAccessCode),

  byId: oc
    .route({
      method: 'GET',
      path: '/chat/{id}',
      summary: 'Get chat by id',
    })
    .input(z.object({ id: z.string() }))
    .output(chatDetails),

  includedPeople: oc
    .route({
      method: 'GET',
      path: '/chat/{id}/included-people',
      summary: 'Get chat included people',
    })
    .input(z.object({ id: z.string() }))
    .output(paginated(includedPerson)),

  addIncludedPerson: oc
    .route({
      method: 'POST',
      path: '/chat/{id}/included-people',
      summary: 'Add a person to a chat',
    })
    .input(
      z.object({
        id: z.string(),
        personId: z.string(),
      }),
    )
    .output(includedPerson),

  addIncludedPeopleBulk: oc
    .route({
      method: 'POST',
      path: '/chat/{id}/included-people/bulk-add',
      summary: 'Add multiple people to a chat',
    })
    .input(
      z.object({
        id: z.string(),
        people: reference.array(),
      }),
    )
    .output(includedPerson.array()),

  removeIncludedPerson: oc
    .route({
      method: 'DELETE',
      path: '/chat/{id}/included-people/{relationId}',
      summary: 'Remove a person from a chat',
    })
    .input(
      z.object({
        id: z.string(),
        relationId: z.string(),
      }),
    )
    .output(includedPerson),

  removeIncludedPeopleBulk: oc
    .route({
      method: 'POST',
      path: '/chat/{id}/included-people/bulk-remove',
      summary: 'Remove multiple people from a chat',
    })
    .input(
      z.object({
        id: z.string(),
        includedPeople: reference.array(),
      }),
    )
    .output(includedPerson.array()),

  update: oc
    .route({
      method: 'PATCH',
      path: '/chat/{id}',
      summary: 'Update chat by id',
    })
    .input(
      z.object({
        id: z.string(),
        ...updateChat.shape,
      }),
    )
    .output(chat),

  sendMessage: oc
    .route({
      method: 'POST',
      path: '/chat/{id}/message',
      summary: 'Send chat by id',
    })
    .input(
      z.object({
        id: z.string(),
        ...sendChat.shape,
      }),
    )
    .output(chatTimelineEntry),

  startChat: oc
    .route({
      method: 'POST',
      path: '/chat',
      summary: 'Start new chat',
    })
    .input(postChat)
    .output(chat),

  getMessages: oc
    .route({
      method: 'GET',
      path: '/chat/{id}/timeline',
      summary: 'Get chat timeline by chat id',
    })
    .input(getChatMessages)
    .output(paginated(chatTimelineEntry)),

  pin: oc
    .route({
      method: 'POST',
      path: '/chat/{id}/pin',
      summary: 'Pin a chat to the top',
    })
    .input(reference)
    .output(chat),

  unpin: oc
    .route({
      method: 'POST',
      path: '/chat/{id}/unpin',
      summary: 'Unpin a chat from the top',
    })
    .input(reference)
    .output(chat),
  read: oc
    .route({
      method: 'POST',
      path: '/chat/{id}/read',
      summary: 'Mark a chat as read',
    })
    .input(reference)
    .output(chat),
};

export default chatContract;
